import type { StateCreator } from 'zustand';
import type { GeneratedVideo, VideoSSEEvent } from '../types';
import { videoApi } from '../services/videoApi';
import { api } from '../services/api';
import type { GenerateVideoRequest } from '../services/videoApi';

export interface VideoSlice {
  videos: GeneratedVideo[];
  videosTotal: number;
  videosLoading: boolean;
  videosGenerating: Record<number, boolean>;

  fetchVideos: (params?: { strategy_id?: number; status?: string }) => Promise<void>;
  generateVideo: (body: GenerateVideoRequest) => Promise<{ video_id: number }>;
  generateFromCarousel: (generationId: number, musicId?: number) => Promise<{ video_id: number }>;
  generateVideoFromCarousel: (strategyId: number, generationId: number, musicId?: number, transitionType?: string) => Promise<{ video_id: number }>;
  deleteVideo: (id: number) => Promise<void>;
  publishVideo: (id: number, caption?: string) => Promise<{ permalink: string }>;
  handleVideoSSE: (event: VideoSSEEvent) => void;
}

export const createVideoSlice: StateCreator<VideoSlice, [], [], VideoSlice> = (set, _get) => ({
  videos: [],
  videosTotal: 0,
  videosLoading: false,
  videosGenerating: {},

  fetchVideos: async (params = {}) => {
    set({ videosLoading: true });
    try {
      const result = await videoApi.listVideos(params);
      set({ videos: result.videos, videosTotal: result.total });
    } finally {
      set({ videosLoading: false });
    }
  },

  generateVideo: async (body: GenerateVideoRequest) => {
    const result = await videoApi.generate(body);
    set((s) => ({ videosGenerating: { ...s.videosGenerating, [result.video_id]: true } }));
    return result;
  },

  generateFromCarousel: async (generationId: number, musicId?: number) => {
    const result = await videoApi.generateFromCarousel(generationId, musicId);
    set((s) => ({ videosGenerating: { ...s.videosGenerating, [result.video_id]: true } }));
    return result;
  },

  generateVideoFromCarousel: async (strategyId: number, generationId: number, musicId?: number, transitionType?: string) => {
    const result = await api.post<{ video_id: number; status: string }>(
      `/content-studio/${strategyId}/generations/${generationId}/export-video`,
      { music_id: musicId, transition_type: transitionType || 'slide' },
    );
    set((s) => ({ videosGenerating: { ...s.videosGenerating, [result.video_id]: true } }));
    return result;
  },

  deleteVideo: async (id: number) => {
    await videoApi.deleteVideo(id);
    set((s) => ({ videos: s.videos.filter((v) => v.id !== id) }));
  },

  publishVideo: async (id: number, caption?: string) => {
    return await videoApi.publishReel(id, caption);
  },

  handleVideoSSE: (event: VideoSSEEvent) => {
    const { video_id, status } = event;
    set((s) => {
      const updated = s.videos.map((v) =>
        v.id === video_id
          ? { ...v, status: status as GeneratedVideo['status'], drive_url: event.drive_url ?? v.drive_url }
          : v,
      );
      const generating = { ...s.videosGenerating };
      if (status === 'ready' || status === 'error') {
        delete generating[video_id];
      }
      return { videos: updated, videosGenerating: generating };
    });
  },
});
